import { ERROR_SUFFIX, LOADING_SUFFIX, SHOW_MORE_SUFFIX, TREE_ID_SEP } from '../constants';

/** A `__loading` placeholder row. */
export function isPlaceholder(id: string): boolean {
  return id.endsWith(LOADING_SUFFIX);
}

/** A `__show_more` pagination row. */
export function isShowMore(id: string): boolean {
  return id.endsWith(SHOW_MORE_SUFFIX);
}

/** A `__error` row shown when a parent's children failed to load. */
export function isErrorRow(id: string): boolean {
  return id.endsWith(ERROR_SUFFIX);
}

/** A real data node */
export function isRealNode(id: string): boolean {
  return !isPlaceholder(id) && !isShowMore(id) && !isErrorRow(id);
}

export function showMoreId(parentId: string): string {
  return `${parentId}${SHOW_MORE_SUFFIX}`;
}

export function errorId(parentId: string): string {
  return `${parentId}${ERROR_SUFFIX}`;
}

/**
 * A tree id is a `::`-joined chain of dataNodeIds from the root down to the
 * node (see `makeTreeId`). A root node therefore has no separator.
 */
export function isRootTreeId(treeId: string): boolean {
  return !treeId.includes(TREE_ID_SEP);
}

/** True when `treeId` is `ancestorId` itself or one of its descendants. */
export function isSelfOrDescendant(treeId: string, ancestorId: string): boolean {
  return treeId === ancestorId || treeId.startsWith(`${ancestorId}${TREE_ID_SEP}`);
}

/** The tree id of the node's immediate parent, or `null` for a root node. */
export function getParentTreeId(treeId: string): string | null {
  const lastSep = treeId.lastIndexOf(TREE_ID_SEP);
  return lastSep === -1 ? null : treeId.slice(0, lastSep);
}

/** The backend dataNodeId encoded as the last segment of a tree id. */
export function getDataNodeIdFromTreeId(treeId: string): string {
  const lastSep = treeId.lastIndexOf(TREE_ID_SEP);
  return lastSep === -1 ? treeId : treeId.slice(lastSep + TREE_ID_SEP.length);
}

/** Root-to-node chain of tree ids (inclusive). */
export function getAncestorTreeIds(treeId: string): string[] {
  const ids: string[] = [];
  let current: string | null = treeId;
  while (current) {
    ids.unshift(current);
    current = getParentTreeId(current);
  }
  return ids;
}
